import { MouseEventHandler, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryParams } from './queryParams';

/**
 * Returns current absolute path with given query params (over)written into it.
 *
 * Current location: /some/path?param1=A&param2=B
 * useHrefWithQueryParams({ param2: C, param3: D }) -> /some/path?param1=A&param2=C&param3=D
 *
 * @param params
 */
export const useHrefWithQueryParams = (params?: Partial<QueryParams>) => {
  const { pathname, search } = useLocation();

  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const paramsSerialized = new URLSearchParams(params as QueryParams<string>);

  return useMemo(() => {
    const searchParams = new URLSearchParams(search);

    if (paramsRef.current) {
      for (const [key, value] of Object.entries(paramsRef.current)) {
        if (value === undefined) {
          searchParams.delete(key);
        } else {
          searchParams.set(key, value.toString());
        }
      }
    }

    return `${pathname}${searchParams ? `?${searchParams}` : ''}`;
  }, [pathname, search, paramsSerialized]);
};

/**
 * Prevents default behavior of the anchor click event — avoids native navigation.
 * Does not apply while meta key is pressed — allows native navigation through new window.
 *
 * @param handler
 */
export const suppressHref =
  (
    handler: MouseEventHandler<HTMLAnchorElement>
  ): MouseEventHandler<HTMLAnchorElement> =>
  (event) => {
    if (!event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      handler(event);
    }
  };
